import {inject, Injectable, model, signal} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {
  FileUploadDto,
  HospitalArbitraryStep2Dto,
  Page, Chart,
  ReportCondition,
  ResultArbitraryReportDto,
  SrmHospitalArbitraryDto,
  SrmHospitalArbitraryReportDto,
  SrmHospitalArbitrarySearchParam
} from "../../generated-model/model";
import {Observable} from "rxjs";
import {environment} from "../../environments/environment";
import {PageRequest} from "../app.constants";
import {IgnoreNullHttpParams} from "../shared/commons/Ignore-null-http-params";
import {StringUtils} from "../shared/commons/gt-ng-datepicker/utils/string-utils";
import {saveAs} from "file-saver";

@Injectable({providedIn: "root"})
export class SrmHospitalArbitraryResource {
  http = inject(HttpClient);
  params = signal(<SrmHospitalArbitrarySearchParam>{});
  search(params: SrmHospitalArbitrarySearchParam, pageRequest: PageRequest): Observable<Page<SrmHospitalArbitraryDto>> {
    let criteria: SrmHospitalArbitrarySearchParam = JSON.parse(JSON.stringify(params));
    if (criteria.hospitalType) {
      criteria.hospitalType.subTypes = null;
    }
    const param = IgnoreNullHttpParams.fromObject({...criteria, ...pageRequest}).toHttpParam()
    return this.http.get<Page<SrmHospitalArbitraryDto>>(`${environment.serverUrl}/api/srm-hospital-arbitrary/search`, {params: param});
  }

  /**
   * เปลี่ยนสถานะจาก ระบบแจ้งเตือนอัตโนมัติให้สปสช.เขต -> อยู่ระหว่างการตรวจสอบของสปสช.เขต
   */
  newTaskDownloadXls(id: number): Observable<SrmHospitalArbitraryDto> {
    return this.http.patch<SrmHospitalArbitraryDto>(`${environment.serverUrl}/api/srm-hospital-arbitrary/${id}/new-task-download-xls`, {});
  }

  /**
   * เปลี่ยนสถานะจาก สปสช.เขต รายงานผลการออกติดตาม -> ฝ่ายทะเบียนตรวจสอบผลรายงานการออกติดตาม
   */
  newTaskDownloadResultXls(id: number): Observable<SrmHospitalArbitraryDto> {
    return this.http.patch(`${environment.serverUrl}/api/srm-hospital-arbitrary/${id}/new-task-download-result-xls`, {});
  }

  /**
   * อยู่ระหว่างการตรวจสอบของสปสช.เขต -> สรุปผลการตรวจสอบ
   */
  finish(id: number): Observable<SrmHospitalArbitraryDto> {
    return this.http.patch(`${environment.serverUrl}/api/srm-hospital-arbitrary/${id}/finish`, {});
  }

  downloadExcel(condition: ReportCondition): Observable<Blob> {
    condition.fileType = 'xlsx'
    const params = IgnoreNullHttpParams.fromObject(condition);
    return this.http.get(`${environment.serverUrl}/api/report/export`, {params: params.toHttpParam(), responseType: 'blob'});
  }

  /**
   * validate ส่งรายงานผลการตรวจสอบ
   */
  prepareReport(request: SrmHospitalArbitraryReportDto): Observable<ResultArbitraryReportDto> {
    return this.http.post<ResultArbitraryReportDto>(`${environment.serverUrl}/api/srm-hospital-arbitrary/prepare-report`, request);
  }

  /**
   * บันทึก
   */
  saveArbitraryReport(request: SrmHospitalArbitraryReportDto): Observable<SrmHospitalArbitraryReportDto> {
    return this.http.post<SrmHospitalArbitraryReportDto>(`${environment.serverUrl}/api/srm-hospital-arbitrary/${request.srmHospitalArbitraryId}/save-report`, request);
  }

  downloadMinio(file: FileUploadDto): Observable<Blob> {
    return this.http.get(`${environment.serverUrl}/public/api/minio/download/${file.bucket}/${file.filename}`, {responseType: 'blob'});
  }

  /**
   * เปลี่ยนสถานะจาก ฝ่ายทะเบียนตรวจสอบผลรายงานการออกติดตาม -> อยู่ระหว่างการตรวจสอบของฝ่ายทะเบียน
   */
  saveTaskDetailsStep1(id: number): Observable<void> {
    return this.http.patch<void>(`${environment.serverUrl}/api/srm-hospital-arbitrary/${id}/task-details-step1`, {});
  }

  saveTaskDetailsStep2(request: HospitalArbitraryStep2Dto): Observable<void> {
    return this.http.post<void>(`${environment.serverUrl}/api/srm-hospital-arbitrary/task-details-step2`, request);
  }


  downloadDataTableExcel(param: SrmHospitalArbitrarySearchParam) {
    let reportName: string = `ข้อมูลการลงทะเบียนที่ผิดปกติ_ตามหน่วยบริการ_${StringUtils.getReportFileName()}`
    let condition: ReportCondition = {
      fileType: 'xlsx',
      nhsoZone: param.zone?.id,
      provinceId: param.province?.id,
      hTypeId: param.hospitalType?.id,
      hSubTypeId: param.hospitalSubType?.id,
      round: param.round,
      arbitraryType: param.arbitraryType,
      arbitraryStatus: param.arbitraryStatus,
      year: param.year,
      month: param.month,
      hmainOp: param.hmainOp?.hcode,
      reportPath : 'hospital_arbitrary.jasper',
      budgetYear: param.budgetYear,
      reportName: reportName
    }
    const params = IgnoreNullHttpParams.fromObject(condition);
    this.http.get(`${environment.serverUrl}/api/report/export`, {params: params.toHttpParam(), responseType: 'blob'})
      .subscribe(e => saveAs(new Blob([e]), `${reportName}.${condition.fileType}`));
  }

  searchHospitalArbitraryPie(params: SrmHospitalArbitrarySearchParam): Observable<Chart[]> {
    const param = IgnoreNullHttpParams.fromObject(params).toHttpParam()
    return this.http.get<Chart[]>(`${environment.serverUrl}/api/srm-hospital-arbitrary/summary-arbitrary-pie`, {params: param});
  }

  searchHospitalArbitraryBar(params: SrmHospitalArbitrarySearchParam): Observable<Chart[]> {
    const param = IgnoreNullHttpParams.fromObject(params).toHttpParam()
    return this.http.get<Chart[]>(`${environment.serverUrl}/api/srm-hospital-arbitrary/summary-arbitrary-bar`, {params: param});
  }

  downloadSummary(params: SrmHospitalArbitrarySearchParam) {

    let reportName: string = `ข้อมูลสรุปผลการดำเนินงานแยกตามสถานะ_${StringUtils.getReportFileName()}`
    let condition: ReportCondition = {
      year: params.year,
      month: params.month,
      round: params.round,
      arbitraryType: params.arbitraryType,
      nhsoZone: params.zone?.id,
      fileType: 'xlsx',
      budgetYear: params.budgetYear,
      reportPath : 'hospital_arbitrary_summary.jasper',
    }
    this.http.get(`${environment.serverUrl}/api/report/export`, {params: IgnoreNullHttpParams.fromObject(condition).toHttpParam(), responseType: 'blob'})
      .subscribe(e => saveAs(new Blob([e]), `${reportName}.${condition.fileType}`));
  }
}
